/**
 * StudioResultModal.js
 *
 * Full-screen overlay modal that displays the Requirement Analysis result:
 * valid/invalid badge, score, reasons, gaps, and structured summary.
 */
import React from 'react';
import {
  exportStudioMarkdownAsDocx,
  exportStudioMarkdownAsPdf,
} from '../../services/studioExport.service';

function summarizeText(value, max = 220) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return 'Not provided';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 3).trim()}...`;
}

function firstSentence(value, max = 180) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return 'Not provided';
  const parts = text.split(/(?<=[.!?])\s+/);
  return summarizeText(parts[0] || text, max);
}

function summarizeSystemScope(text) {
  const src = String(text || '');
  const tags = [];
  if (/mobile application|ios|android/i.test(src)) tags.push('Mobile app (iOS/Android)');
  if (/web application|browser|desktop access/i.test(src)) tags.push('Web app');
  if (/rest api/i.test(src)) tags.push('REST API');
  if (/websocket/i.test(src)) tags.push('WebSocket real-time');
  if (/ai companion/i.test(src)) tags.push('AI companion service');
  if (/therapist portal/i.test(src)) tags.push('Therapist portal');
  if (/notification service|sms|push|email/i.test(src)) tags.push('Notification service');
  if (!tags.length) return firstSentence(src, 210);
  return tags.join(' | ');
}

function summarizePersonas(text) {
  const src = String(text || '');
  const segments = [...src.matchAll(/(?:Primary|Secondary|Tertiary)\s+Segment\s+[—-]\s*([^:]+):/gi)]
    .map((m) => summarizeText(m[1], 70));

  const parts = [];
  if (segments.length) parts.push(`Segments: ${segments.join(', ')}`);
  if (/not a crisis intervention service|helpline|conversation is suspended/i.test(src)) {
    parts.push('Crisis policy: helpline escalation + AI chat suspension');
  }

  return parts.length ? parts.join(' | ') : firstSentence(src, 210);
}

function summarizeScale(text) {
  const src = String(text || '');
  const registered = [...src.matchAll(/Registered users:\s*([\d,]+)/gi)].map((m) => m[1]);
  const concurrent = [...src.matchAll(/Concurrent users:\s*([\d,]+)/gi)].map((m) => m[1]);
  const regions = [...src.matchAll(/Regions:\s*([^\n\r]+?)(?=\s+At\s+|\s+---|$)/gi)].map((m) => summarizeText(m[1], 60));

  const parts = [];
  if (registered.length >= 2) parts.push(`Registered users: ${registered[0]} -> ${registered[registered.length - 1]}`);
  if (concurrent.length >= 2) parts.push(`Concurrent users: ${concurrent[0]} -> ${concurrent[concurrent.length - 1]}`);
  if (regions.length >= 2) parts.push(`Regions: ${regions[0]} -> ${regions[regions.length - 1]}`);

  return parts.length ? parts.join(' | ') : firstSentence(src, 210);
}

function summarizeSecurity(text) {
  const src = String(text || '');
  const standards = ['HIPAA', 'GDPR', 'CCPA', 'SOC 2', 'SOC2', 'ISO 27001']
    .filter((item, idx, arr) => src.toUpperCase().includes(item.replace(' ', '')) || src.toUpperCase().includes(arr[idx].toUpperCase()));

  const normalizedStandards = [];
  standards.forEach((s) => {
    const canonical = s === 'SOC2' ? 'SOC 2' : s;
    if (!normalizedStandards.includes(canonical)) normalizedStandards.push(canonical);
  });

  const securityParts = [];
  if (normalizedStandards.length) securityParts.push(`Standards: ${normalizedStandards.join(', ')}`);
  if (/aes-?256/i.test(src) || /tls\s*1\.3/i.test(src)) securityParts.push('Encryption: AES-256 at rest + TLS 1.3 in transit');
  if (/right to erasure|data export|deletion/i.test(src)) securityParts.push('Privacy rights: export + deletion workflows');

  return securityParts.length ? securityParts.join(' | ') : firstSentence(src, 210);
}

function summarizeFunctional(text) {
  const src = String(text || '');
  const modules = [...src.matchAll(/MODULE\s*\d+\s*[—-]\s*([^\n:.]+)/gi)].map((m) => summarizeText(m[1], 45));
  if (modules.length) {
    return `Modules: ${modules.slice(0, 5).join(', ')}`;
  }

  const features = [];
  if (/onboarding/i.test(src)) features.push('User onboarding');
  if (/mood/i.test(src)) features.push('Daily mood check-in');
  if (/ai companion/i.test(src)) features.push('AI companion conversation');
  if (/therapist/i.test(src)) features.push('Therapist workflows');
  if (features.length) return `Core capabilities: ${features.join(', ')}`;

  return firstSentence(src, 210);
}

function summarizeDoD(text) {
  const src = String(text || '');
  const categories = [...src.matchAll(/\b([A-Z][A-Z\s/&()-]{5,}):/g)]
    .map((m) => m[1].trim())
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .slice(0, 8);

  if (categories.length) return `Gate categories: ${categories.join(', ')}`;
  return firstSentence(src, 210);
}

function toBase64Url(input = '') {
  const bytes = new TextEncoder().encode(String(input || ''));
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function extractMermaidRaw(content = '') {
  if (!content) return '';
  return String(content)
    .replace(/```mermaid/gi, '')
    .replace(/```/g, '')
    .trim();
}

function getDiagramImageCandidates(diagram = {}) {
  const candidates = [];

  if (Array.isArray(diagram.image_urls)) {
    candidates.push(...diagram.image_urls);
  }

  if (diagram.image_url) {
    candidates.push(diagram.image_url);
  }

  const raw = extractMermaidRaw(diagram.content);
  if (raw) {
    const encoded = toBase64Url(raw);
    candidates.push(`https://mermaid.ink/img/${encoded}`);
    candidates.push(`https://quickchart.io/mermaid?graph=${encodeURIComponent(raw)}`);
  }

  const priority = (url) => {
    const u = String(url || '').toLowerCase();
    if (u.includes('mermaid.ink')) return 0;
    if (u.includes('quickchart.io')) return 2;
    return 1;
  };

  const seen = new Set();
  return candidates
    .map((u) => String(u || '').trim())
    .filter((u) => /^https?:\/\//i.test(u))
    .sort((a, b) => priority(a) - priority(b))
    .filter((u) => {
      if (seen.has(u)) return false;
      seen.add(u);
      return true;
    });
}

function toTitleCase(value = '') {
  return String(value || '')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function getDiagramDisplayName(diagramOrName = {}) {
  if (diagramOrName && typeof diagramOrName === 'object') {
    if (diagramOrName.display_name) return String(diagramOrName.display_name);
    if (diagramOrName.name) return getDiagramDisplayName(diagramOrName.name);
  }

  const raw = String(diagramOrName || '').replace(/\.mmd$/i, '').trim();
  if (!raw) return 'Design Diagram';
  return toTitleCase(raw.replace(/[_\-]+/g, ' '));
}

function getDiagramExplanation(diagramOrName = {}) {
  if (!diagramOrName) return '';
  if (typeof diagramOrName === 'object') {
    return String(diagramOrName.explanation || '').trim();
  }
  return '';
}

function DiagramPreviewCard({ diagram }) {
  const candidates = React.useMemo(() => getDiagramImageCandidates(diagram), [diagram]);
  const [imageIdx, setImageIdx] = React.useState(0);
  const [renderFailed, setRenderFailed] = React.useState(false);
  const currentSrc = candidates[imageIdx] || '';
  const displayName = getDiagramDisplayName(diagram);
  const explanation = getDiagramExplanation(diagram);

  React.useEffect(() => {
    setImageIdx(0);
    setRenderFailed(false);
  }, [diagram]);

  return (
    <div className="studio-design-card">
      <div className="studio-design-title">{displayName}</div>
      {explanation && <div className="studio-design-explanation">{explanation}</div>}
      {currentSrc && (
        <a href={currentSrc} target="_blank" rel="noreferrer" className="studio-design-link">
          Open Diagram Image
        </a>
      )}
      {currentSrc && (
        <img
          src={currentSrc}
          alt={displayName || 'Design diagram'}
          className="studio-design-img"
          loading="lazy"
          onLoad={() => {
            setRenderFailed(false);
          }}
          onError={() => {
            if (imageIdx < candidates.length - 1) {
              setImageIdx((prev) => prev + 1);
            } else {
              setRenderFailed(true);
            }
          }}
        />
      )}
      {(!currentSrc || renderFailed) && diagram.content && (
        <pre className="studio-design-pre">{diagram.content}</pre>
      )}
    </div>
  );
}

function DocPreviewContent({ content, diagramByName = {} }) {
  const blocks = React.useMemo(() => {
    const lines = String(content || '').split('\n');
    const parsed = [];
    let i = 0;

    while (i < lines.length) {
      if (/^\s*```mermaid\s*$/i.test(lines[i])) {
        const mermaidLines = [];
        i += 1;
        while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) {
          mermaidLines.push(lines[i]);
          i += 1;
        }
        if (i < lines.length) i += 1;
        parsed.push({ type: 'mermaid', raw: mermaidLines.join('\n').trim() });
        continue;
      }

      const textLines = [];
      while (i < lines.length && !/^\s*```mermaid\s*$/i.test(lines[i])) {
        textLines.push(lines[i]);
        i += 1;
      }
      parsed.push({ type: 'text', text: textLines.join('\n') });
    }

    return parsed;
  }, [content]);

  const diagrams = React.useMemo(() => Object.values(diagramByName || {}), [diagramByName]);

  return (
    <div className="studio-doc-preview-wrap">
      {blocks.map((block, idx) => {
        if (block.type === 'mermaid') {
          const matched = diagrams.find((d) => extractMermaidRaw(d?.content) === block.raw);
          const inlineDiagram = matched || {
            name: `inline_mermaid_${idx + 1}.mmd`,
            display_name: `Inline Mermaid ${idx + 1}`,
            content: `\`\`\`mermaid\n${block.raw}\n\`\`\``,
          };

          return (
            <div key={`mermaid_${idx}`} className="studio-doc-mermaid-block">
              <DiagramPreviewCard diagram={inlineDiagram} />
            </div>
          );
        }

        const text = String(block.text || '');
        if (!text.trim()) return null;
        return <pre key={`text_${idx}`} className="studio-doc-preview">{text}</pre>;
      })}
    </div>
  );
}

function StudioResultModal({ result, onClose, onFinalizePhase, phaseLabel }) {
  if (!result) return null;

  const { valid, score, reasons = [], gaps = [], summary = {} } = result;
  const analysisMode = result.analysis_mode || 'model';
  const isDeterministic =
    analysisMode === 'deterministic'
    || reasons.some((r) => /deterministic|unavailable|fallback/i.test(String(r || '')));
  const pct = Math.round(((score || 0) / 10) * 100);
  const requirementDocument = result.requirement_document || null;
  const designDocument = result.design_document || null;
  const documentationBundle = result.documentation_bundle || null;
  const designDocuments = Array.isArray(designDocument?.design_documents)
    ? designDocument.design_documents.filter((d) => d && d.title)
    : [];
  const allDiagramArtifacts = Array.isArray(designDocument?.diagram_artifacts)
    ? designDocument.diagram_artifacts.filter(Boolean)
    : [];
  const diagramByName = allDiagramArtifacts.reduce((acc, item) => {
    if (item?.name) acc[item.name] = item;
    return acc;
  }, {});
  const resolvedDesignDocs = (() => {
    if (!designDocuments.length) return [];
    const ordered = [];
    const seen = new Set();
    const priority = ['hld', 'lld'];
    priority.forEach((id) => {
      const match = designDocuments.find((doc) => doc.id === id);
      if (match && !seen.has(match.id || match.file_name || match.title)) {
        ordered.push(match);
        seen.add(match.id || match.file_name || match.title);
      }
    });
    if (ordered.length < 2) {
      designDocuments.forEach((doc) => {
        const key = doc.id || doc.file_name || doc.title;
        if (!seen.has(key) && ordered.length < 2) {
          ordered.push(doc);
          seen.add(key);
        }
      });
    }
    return ordered;
  })();
  const activePhase = result.current_phase
    || (designDocument ? 'design' : (documentationBundle ? 'documentation' : 'requirements'));
  const resolvedPhaseLabel = phaseLabel || (activePhase.charAt(0).toUpperCase() + activePhase.slice(1));

  const ts = summary.tech_stack || {};

  const buildDefaultDocument = () => {
    const title = `Software Requirements Document - ${summary.project_name || 'Project'}`;
    const lines = [
      `# ${title}`,
      '',
      `Readiness Score: ${score || 0}/10 (${valid ? 'Valid' : 'Needs clarification'})`,
      '',
      '## Title',
      summary.project_name || 'Project',
      '',
      '## Description',
      summary.overview || 'Requirement summary is not available yet.',
      '',
      '## Background / Notes',
      `- System Type: ${summary.system_type || 'Not provided'}`,
      `- Target Users: ${summary.target_users || 'Not provided'}`,
      `- Scale Expectations: ${summary.expected_scale || 'Not provided'}`,
      `- Security & Compliance: ${summary.security_compliance || 'Not provided'}`,
      '',
      '## User Role (Personas)',
      summary.target_users || 'Not provided',
      '',
      '## User Goal',
      summary.end_goal || 'Not provided',
      '',
      '## Reason / Benefits',
      `- Expected outcome: ${summary.end_goal || 'Not provided'}`,
      `- Functional value: ${summary.functional_requirements || 'Not provided'}`,
      '',
      '## Acceptance Criteria',
      `1. Functional requirements are defined: ${summary.functional_requirements || 'Not provided'}`,
      `2. Non-functional requirements are defined: ${summary.non_functional || 'Not provided'}`,
      `3. Definition of Done is defined: ${summary.definition_of_done || 'Not provided'}`,
      '',
      '## Additional Info',
      `- Risks: ${Array.isArray(summary.risks) && summary.risks.length ? summary.risks.join(' | ') : 'Not provided'}`,
      '',
      '## Assessment',
      ...(reasons.length ? reasons.map((r) => `- ${r}`) : ['- No assessment notes available.']),
      '',
      '## Gaps',
      ...(gaps.length ? gaps.map((g) => `- ${g}`) : ['- No critical gaps listed.']),
    ];
    return {
      title,
      file_name: 'requirements_project.md',
      content: lines.join('\n'),
    };
  };

  const phaseArtifact = activePhase === 'design'
    ? designDocument
    : activePhase === 'documentation'
      ? documentationBundle
      : requirementDocument;
  const docArtifact = phaseArtifact || requirementDocument || designDocument || documentationBundle || buildDefaultDocument();
  const docSectionTitle = activePhase === 'design'
    ? 'Design Document'
    : activePhase === 'documentation'
      ? 'Documentation Bundle'
      : 'Requirement Document';
  const validityLabel = valid ? `✅ Valid ${resolvedPhaseLabel}` : `❌ Incomplete ${resolvedPhaseLabel}`;
  const isDesignTwoFileMode = activePhase === 'design' && resolvedDesignDocs.length > 0;

  const diagramsForDoc = (doc = {}) => {
    const refs = Array.isArray(doc.diagram_artifacts) ? doc.diagram_artifacts : [];
    if (!refs.length) return allDiagramArtifacts;
    return refs
      .map((name) => diagramByName[name])
      .filter(Boolean);
  };

  const requirementSectionSummaries = [
    ['Title', summary.project_name],
    ['Description', firstSentence(summary.overview, 220)],
    ['Background / Notes', [
      `System: ${summarizeSystemScope(summary.system_type)}`,
      `Scale: ${summarizeScale(summary.expected_scale)}`,
      `Security: ${summarizeSecurity(summary.security_compliance)}`,
    ].join(' | ')],
    ['User Role (Personas)', summarizePersonas(summary.target_users)],
    ['User Goal', firstSentence(summary.end_goal, 220)],
    ['Reason / Benefits', summarizeFunctional(summary.functional_requirements)],
    ['Acceptance Criteria', [
      summary.functional_requirements ? 'Functional scope captured' : 'Functional scope missing',
      summary.non_functional ? 'NFR captured' : 'NFR missing',
      summary.definition_of_done ? 'DoD captured' : 'DoD missing',
      summary.security_compliance ? 'Security/compliance captured' : 'Security/compliance missing',
    ].join(' | ')],
    ['Additional Info', [
      `DoD: ${summarizeDoD(summary.definition_of_done)}`,
      `Risks: ${Array.isArray(summary.risks) && summary.risks.length ? summary.risks.join(' | ') : 'None'}`,
    ].join(' | ')],
  ];

  const downloadTextFile = (content, fileName, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadMarkdown = () => {
    if (isDesignTwoFileMode) {
      resolvedDesignDocs.forEach((doc) => {
        const fileName = (doc.file_name || `${doc.id || 'design'}.md`).replace(/\.(txt|doc|pdf)$/i, '.md');
        downloadTextFile(doc.content || '', fileName, 'text/markdown;charset=utf-8');
      });
      return;
    }

    const fileName = (docArtifact.file_name || 'requirements_document.md').replace(/\.(txt|doc|pdf)$/i, '.md');
    downloadTextFile(docArtifact.content || '', fileName, 'text/markdown;charset=utf-8');
  };

  const handleDownloadDoc = async () => {
    if (isDesignTwoFileMode) {
      for (const doc of resolvedDesignDocs) {
        // Export exactly two design deliverables: high-level and low-level.
        // eslint-disable-next-line no-await-in-loop
        await exportStudioMarkdownAsDocx(doc, diagramsForDoc(doc));
      }
      return;
    }

    await exportStudioMarkdownAsDocx(docArtifact, designDocument?.diagram_artifacts || []);
  };

  const handleDownloadPdf = async () => {
    if (isDesignTwoFileMode) {
      for (const doc of resolvedDesignDocs) {
        // Export exactly two design deliverables: high-level and low-level.
        // eslint-disable-next-line no-await-in-loop
        await exportStudioMarkdownAsPdf(doc, diagramsForDoc(doc));
      }
      return;
    }

    await exportStudioMarkdownAsPdf(docArtifact, designDocument?.diagram_artifacts || []);
  };

  return (
    <div className="studio-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="studio-modal">
        {/* Header */}
        <div className="studio-modal-header">
          <div className="studio-modal-title">📊 {resolvedPhaseLabel} Analysis</div>
          <div className="studio-modal-actions">
            <button className="studio-modal-action-btn" onClick={handleDownloadMarkdown}>{isDesignTwoFileMode ? 'Download MD x2' : 'Download MD'}</button>
            <button className="studio-modal-action-btn" onClick={handleDownloadDoc}>{isDesignTwoFileMode ? 'Download DOCX x2' : 'Download DOCX'}</button>
            <button className="studio-modal-action-btn primary" onClick={handleDownloadPdf}>{isDesignTwoFileMode ? 'Download PDF x2' : 'Download PDF'}</button>
            {onFinalizePhase && (
              <button className="studio-modal-action-btn success" onClick={onFinalizePhase}>
                Finalize {phaseLabel}
              </button>
            )}
          </div>
          <button className="studio-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="studio-modal-body">
          {/* Score + badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
            <span className={`studio-valid-badge ${valid ? 'valid' : 'invalid'}`} style={{ fontSize: '0.9rem', padding: '5px 14px' }}>
              {validityLabel}
            </span>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#64748b', marginBottom: 4 }}>
                <span>Completeness Score</span>
                <strong style={{ color: '#0f172a' }}>{score}/10</strong>
              </div>
              <div className="studio-score-bar" style={{ height: 8 }}>
                <div className="studio-score-fill" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>

          {/* Summary overview */}
          {summary.project_name && (
            <div className="studio-modal-section">
              <div className="studio-modal-section-title">Project</div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                {summary.project_name}
              </div>
              {summary.overview && (
                <p style={{ fontSize: '0.86rem', color: '#475569', margin: 0, lineHeight: 1.6 }}>
                  {summary.overview}
                </p>
              )}
            </div>
          )}

          {/* Requirement document preview */}
          {docArtifact?.content && !isDesignTwoFileMode && (
            <div className="studio-modal-section">
              <div className="studio-modal-section-title">{docSectionTitle}</div>
              <details>
                <summary style={{ cursor: 'pointer', color: '#334155', fontWeight: 600, marginBottom: 8 }}>
                  Open Full Document
                </summary>
                <DocPreviewContent content={docArtifact.content} diagramByName={diagramByName} />
              </details>
            </div>
          )}

          {Array.isArray(designDocument?.diagram_artifacts) && designDocument.diagram_artifacts.length > 0 && (
            <div className="studio-modal-section">
              <div className="studio-modal-section-title">Design Diagrams</div>
              <div className="studio-design-grid">
                {designDocument.diagram_artifacts.map((d) => (
                  <DiagramPreviewCard key={`${d.name || 'diagram'}_${d.image_url || ''}`} diagram={d} />
                ))}
              </div>
            </div>
          )}

          {resolvedDesignDocs.length > 0 && (
            <div className="studio-modal-section">
              <div className="studio-modal-section-title">Design Deliverables (High-Level + Low-Level)</div>
              <div className="studio-design-grid">
                {resolvedDesignDocs.map((doc) => (
                  <div className="studio-design-card" key={`${doc.id || doc.file_name || doc.title}`}>
                    <div className="studio-design-title">{doc.title}</div>
                    {doc.file_name && (
                      <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 6 }}>
                        {doc.file_name}
                      </div>
                    )}
                    {Array.isArray(doc.diagram_artifacts) && doc.diagram_artifacts.length > 0 && (
                      <div style={{ fontSize: '0.78rem', color: '#334155', marginBottom: 8 }}>
                        Related diagrams: {doc.diagram_artifacts.map((name) => getDiagramDisplayName(diagramByName[name] || name)).join(', ')}
                      </div>
                    )}
                    {doc.content && (
                      <details>
                        <summary style={{ cursor: 'pointer', color: '#334155', fontWeight: 600 }}>
                          Preview
                        </summary>
                        <DocPreviewContent content={doc.content} diagramByName={diagramByName} />
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Key details grid */}
          <div className="studio-modal-section">
            <div className="studio-modal-section-title">
              {activePhase === 'requirements' ? 'Section Summaries' : 'Details'}
            </div>
            <div className="studio-summary-grid">
              {(activePhase === 'requirements'
                ? requirementSectionSummaries
                : [
                    ['System Type', summary.system_type],
                    ['Target Users', summary.target_users],
                    ['Scale Expectations', summary.expected_scale],
                    ['End Goal', summary.end_goal],
                    ['Functional Requirements', summary.functional_requirements],
                    ['Non-Functional Requirements', summary.non_functional],
                    ['Definition of Done', summary.definition_of_done],
                    ['Security & Compliance', summary.security_compliance],
                  ]
              )
                .filter(([, v]) => v)
                .map(([label, val]) => (
                <div key={label}>
                  <div className="studio-summary-cell-label">{label}</div>
                  <div className="studio-summary-cell-value" title={String(val || '')}>
                    {summarizeText(val, activePhase === 'requirements' ? 260 : 220)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tech stack */}
          {(ts.frontend || ts.backend || ts.database || ts.cloud) && (
            <div className="studio-modal-section">
              <div className="studio-modal-section-title">Tech Stack</div>
              <div className="studio-tag-row">
                {ts.frontend  && <span className="studio-tag">FE: {ts.frontend}</span>}
                {ts.backend   && <span className="studio-tag">BE: {ts.backend}</span>}
                {ts.database  && <span className="studio-tag">DB: {ts.database}</span>}
                {ts.cloud     && <span className="studio-tag">☁️ {ts.cloud}</span>}
              </div>
            </div>
          )}

          {/* Key features */}
          {Array.isArray(summary.key_features) && summary.key_features.length > 0 && (
            <div className="studio-modal-section">
              <div className="studio-modal-section-title">Key Features</div>
              <div className="studio-tag-row">
                {summary.key_features.map((f, i) => (
                  <span key={i} className="studio-tag green">{f}</span>
                ))}
              </div>
            </div>
          )}

          {/* Reasons */}
          {reasons.length > 0 && (
            <div className="studio-modal-section">
              <div className="studio-modal-section-title">Assessment</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 8 }}>
                Mode: {isDeterministic ? 'Deterministic completeness scoring' : 'Model-based analysis'}
              </div>
              {reasons.map((r, i) => (
                <div key={i} className="studio-reason-item">
                  <span>{i + 1}.</span>
                  <span>{r}</span>
                </div>
              ))}
            </div>
          )}

          {/* Gaps */}
          {gaps.length > 0 && (
            <div className="studio-modal-section">
              <div className="studio-modal-section-title">Gaps to Address</div>
              {gaps.map((g, i) => (
                <div key={i} className="studio-reason-item">
                  <span style={{ color: '#f59e0b' }}>⚠️</span>
                  <span>{g}</span>
                </div>
              ))}
            </div>
          )}

          {/* Risks */}
          {Array.isArray(summary.risks) && summary.risks.length > 0 && (
            <div className="studio-modal-section">
              <div className="studio-modal-section-title">Risks</div>
              <div className="studio-tag-row">
                {summary.risks.map((r, i) => (
                  <span key={i} className="studio-tag red">{r}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudioResultModal;
